import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, EDIT, ASC, DESC } from "const"
import { QidModel } from "server/models"
import { hasAccess, StateType, OrderType } from "client/utils"
import {
    PRM_QID_PRODUCT,
    StoreType,
    QidModelFilters,

    LoadAllQidModels,
    SortQidModels,

    setQidModelList,
    setQidModelIds,
    setQidModelSort,
    setQidModelFilters,
} from "client/store"
import {
    Container,
    PageContent,
    Loader,
    Empty,
    Button,

    TableSearchWrapper,
    TableWrapper,
    TableScroller,
    TableContentWrapper,
    TableRow,
    TableHeaderCell,
    TableCell,
    TableCellContentWrapper,
    SortControl,
} from "client/components"

import QidModelSearch from "client/pages/search/qidModelSearch"

import Columns from "./list/columns"
import Rows from "./list/rows"
import { openModel } from "./list/open"

import EditOption from "./editOption"
import EditFailureMode from "./editFailureMode"

import ConfirmDeleteModel from "./confirmDelete/model"
import ConfirmDeleteSafetyFunction from "./confirmDelete/safetyFunction"
import ConfirmDeleteOption from "./confirmDelete/option"
import ConfirmDeleteFailureMode from "./confirmDelete/failureMode"

const Models = () => {
    const product = useSelector((state: StoreType) => state.qidProduct.productView)

    const modelList = useSelector((state: StoreType) => state.qidModel.modelList)
    const changeStatusModel = useSelector((state: StoreType) => state.qidModel.changeStatusModel)

    const sortColumn = useSelector((state: StoreType) => state.qidModel.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.qidModel.sortOrder)
    const filters = useSelector((state: StoreType) => state.qidModel.filters)
    const reloadList = useSelector((state: StoreType) => state.qidModel.reloadList)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    const productEditAccess = hasAccess(PRM_QID_PRODUCT, EDIT)

    useEffect(() => {
        if (!reloadList) return
        if (!product || !product.id) return

        setLoadState(PENDING)
        // show only PUBLISHED rows for users without "edit access"
        LoadAllQidModels(product.id, !productEditAccess)
        .then((res) => {
            dispatch(setQidModelList(res))
            setLoadState(IDLE)
        })
    }, [product, reloadList])

    useEffect(() => {
        const sortedIds = SortQidModels(Object.values(modelList), sortColumn, sortOrder, filters)
        dispatch(setQidModelIds(sortedIds))
    }, [modelList, sortColumn, sortOrder, filters])

    const onSort = (column: keyof QidModel) => {
        if (changeStatusModel) return

        let order: OrderType
        if (column === sortColumn) {
            order = (sortOrder === ASC) ? DESC : ASC
        } else order = ASC
        dispatch(setQidModelSort(column, order))
    }

    const onFilter = (newFilters: QidModelFilters) => {
        dispatch(setQidModelFilters(newFilters))
    }


    const columns = Columns(productEditAccess)
    const rows = Rows(productEditAccess, columns)

    return (
        <Container>
            <PageContent>
                {
                    loadState === PENDING
                    && <Loader />
                }

                <TableSearchWrapper>
                    <QidModelSearch
                        id="modelList"
                        rightOffset={productEditAccess}
                        filters={filters}
                        onFilter={onFilter}
                    />
                    {
                        productEditAccess && (
                            <Button
                                variant="success"
                                onClick={() => openModel(0, dispatch)}
                            >
                                {lang.add}
                            </Button>
                        )
                    }
                </TableSearchWrapper>

                <TableWrapper>
                    <TableScroller>
                        <TableContentWrapper>
                            <thead>
                                <TableRow size="sm" noHover>
                                    {
                                        columns.map((column) => (
                                            <TableHeaderCell
                                                key={column.field}
                                                cellWidth={column.cellWidth || null}
                                                cellMinWidth={column.cellMinWidth || null}
                                                cellMaxWidth={column.cellMaxWidth || null}
                                                onClick={() => column.sortable && onSort(column.field as keyof QidModel)}
                                            >
                                                {
                                                    column.header
                                                    && <span>{column.header}</span>
                                                }
                                                {
                                                    column.sortable
                                                    && (
                                                        <SortControl
                                                            show={sortColumn === column.field}
                                                            direction={sortColumn === column.field ? sortOrder : DESC}
                                                        />
                                                    )
                                                }
                                            </TableHeaderCell>
                                        ))
                                    }
                                </TableRow>
                            </thead>
                            <tbody>
                                { rows }
                                {
                                    rows.length === 0 && (
                                        <TableRow size="sm">
                                            <TableCell colSpan={100}>
                                                <TableCellContentWrapper>
                                                    <Empty />
                                                </TableCellContentWrapper>
                                            </TableCell>
                                        </TableRow>
                                    )
                                }
                            </tbody>
                        </TableContentWrapper>
                    </TableScroller>
                </TableWrapper>

                <EditOption />
                <EditFailureMode />

                <ConfirmDeleteModel />
                <ConfirmDeleteSafetyFunction />
                <ConfirmDeleteOption />
                <ConfirmDeleteFailureMode />
            </PageContent>
        </Container>
    )
}
export default Models
